import { apiClient } from '@/lib/api-client';
import type { Department } from '@/features/hrms/departments/types';

/**
 * Departments API Service
 * Handles all department and organization-related API calls
 */

// API Response types
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DepartmentCreateData {
  name: string;
  headOfDepartment: string;
  headEmail: string;
  headPhone: string;
  location: string;
  budget: number;
  description: string;
}

export interface DepartmentUpdateData extends Partial<DepartmentCreateData> {
  budgetUsed?: number;
}

export interface TeamCreateData {
  departmentId: string;
  name: string;
  teamLead: string;
  memberCount?: number;
}

export interface TeamUpdateData {
  name?: string;
  teamLead?: string;
  memberCount?: number;
}

export interface DepartmentFilters {
  search?: string;
  location?: string;
  minEmployees?: number;
  maxEmployees?: number;
  minBudget?: number;
  maxBudget?: number;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DepartmentStats {
  total: number;
  totalEmployees: number;
  totalTeams: number;
  totalBudget: number;
  totalBudgetUsed: number;
  budgetUtilization: number;
  byLocation: Record<string, number>;
  averageEmployeesPerDepartment: number;
}

export interface Team {
  id: string;
  departmentId: string;
  name: string;
  teamLead: string;
  memberCount: number;
}

class DepartmentsService {
  private basePath = '/hrms/departments';

  /**
   * Get all departments with optional filters
   */
  async getDepartments(filters?: DepartmentFilters): Promise<PaginatedResponse<Department>> {
    return apiClient.get<PaginatedResponse<Department>>(this.basePath, {
      params: filters,
    });
  }

  /**
   * Get a single department by ID
   */
  async getDepartment(id: string): Promise<Department> {
    return apiClient.get<Department>(`${this.basePath}/${id}`);
  }

  /**
   * Create a new department
   */
  async createDepartment(data: DepartmentCreateData): Promise<Department> {
    return apiClient.post<Department>(this.basePath, data);
  }

  /**
   * Update an existing department
   */
  async updateDepartment(id: string, data: DepartmentUpdateData): Promise<Department> {
    return apiClient.patch<Department>(`${this.basePath}/${id}`, data);
  }

  /**
   * Delete a department
   */
  async deleteDepartment(id: string): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/${id}`);
  }

  /**
   * Get department statistics
   */
  async getDepartmentStats(): Promise<DepartmentStats> {
    return apiClient.get(`${this.basePath}/stats`);
  }

  /**
   * Get all teams for a department
   */
  async getDepartmentTeams(departmentId: string): Promise<Team[]> {
    return apiClient.get(`${this.basePath}/${departmentId}/teams`);
  }

  /**
   * Create a new team within a department
   */
  async createTeam(data: TeamCreateData): Promise<Team> {
    return apiClient.post<Team>(`${this.basePath}/${data.departmentId}/teams`, data);
  }

  /**
   * Update a team
   */
  async updateTeam(departmentId: string, teamId: string, data: TeamUpdateData): Promise<Team> {
    return apiClient.patch<Team>(`${this.basePath}/${departmentId}/teams/${teamId}`, data);
  }

  /**
   * Delete a team
   */
  async deleteTeam(departmentId: string, teamId: string): Promise<void> {
    return apiClient.delete<void>(`${this.basePath}/${departmentId}/teams/${teamId}`);
  }

  /**
   * Get employees in a department
   */
  async getDepartmentEmployees(departmentId: string): Promise<{
    employees: Array<{
      id: string;
      name: string;
      role: string;
      teamId?: string;
    }>;
    total: number;
  }> {
    return apiClient.get(`${this.basePath}/${departmentId}/employees`);
  }

  /**
   * Update department budget
   */
  async updateBudget(
    departmentId: string,
    budget: number,
    budgetUsed?: number,
  ): Promise<Department> {
    return apiClient.patch<Department>(`${this.basePath}/${departmentId}/budget`, {
      budget,
      budgetUsed,
    });
  }

  /**
   * Get department hierarchy
   */
  async getDepartmentHierarchy(): Promise<{
    departments: Array<{
      id: string;
      name: string;
      teams: Array<{
        id: string;
        name: string;
        members: number;
      }>;
    }>;
  }> {
    return apiClient.get(`${this.basePath}/hierarchy`);
  }

  /**
   * Export departments to CSV/Excel
   */
  async exportDepartments(format: 'csv' | 'xlsx', filters?: DepartmentFilters): Promise<Blob> {
    const response = await apiClient.getAxiosInstance().get(`${this.basePath}/export`, {
      params: { format, ...filters },
      responseType: 'blob',
    });
    return response.data;
  }
}

// Export singleton instance
export const departmentsService = new DepartmentsService();
